import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import algoliasearch from 'algoliasearch/lite';
import {
  Configure,
  connectHitInsights,
  connectSearchBox,
  connectStateResults,
  Hits,
  Index,
  InstantSearch,
} from 'react-instantsearch-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { aa } from '@repo/utils/algoliaAnalytics';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { useKey } from 'react-use';
import Link from '@repo/ui-components/tailwind/Link';
import { trackSearchViewedEvent } from '@repo/utils/klaviyo';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { LocaleContext } from '@repo/contexts';
import { SiteSettingsContext } from '@repo/contexts/SiteSettingsContext';
import { useLocalizedNavigation } from '@repo/gatsby-localization-helpers';
import SearchForm from './search-form/SearchForm';
import SearchFormHit from './search-form/SearchFormHit';

const CustomSearchFormHit = connectHitInsights(aa)(SearchFormHit);

const ConnectedSearchForm = connectSearchBox(SearchForm);

let firstLoad = true;

const algoliaClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
);

const searchClient = {
  ...algoliaClient,
  search(requests) {
    // https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/js/#detecting-empty-search-requests
    // ! preventing the first empty query to `prime connection` to algolia will mean delay in showing first results to user
    if (firstLoad === true) {
      firstLoad = false;
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }

    if (requests.every(({ params }) => !params.query)) {
      // ! empty requests still need to return formatted response
      // https://www.algolia.com/doc/api-reference/api-methods/search/#response
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }

    return algoliaClient.search(requests).catch(() => {
      /** do nothing */
    });
  },
};

function NavSearch({
  searchIndexes,
  hitsPerPage = 5,
  searchFocusUpdater = 1,
  toggleMobileMenu = () => {},
}) {
  const { tt } = useUiComponentsInternationalContext();
  const [activeSearchState, setActiveSearchState] = useState({});
  const [apiError, setApiError] = useState(false);
  const [focused, setFocused] = useState(false);
  const [numberOfResults, setNumberOfResults] = useState(0);

  const localeContext = useContext(LocaleContext);

  const countryCode = (localeContext?.countryCode || 'US').toLowerCase();

  useKey('Escape', () => {
    if (focused) setFocused(false);
  });

  const { algoliaSearchSettings } = useContext(SiteSettingsContext);
  const navigate = useLocalizedNavigation();

  const {
    blogIndexName,
    getAlgoliaBlogsFilter,
    productIndexName,
    getAlgoliaProductsFilter,
  } = algoliaSearchSettings;

  const searchRes = [];

  const Results = connectStateResults(
    ({ searchState, searchResults, children }) => {
      // Handle tracking only once per new query
      if (
        searchResults &&
        searchState?.query &&
        searchResults.index === 'globalProducts'
      ) {
        const allResults = Array.isArray(searchResults.results)
          ? searchResults.results
          : [searchResults];

        const totalHits = allResults.reduce(
          (sum, result) => sum + (result.nbHits || 0),
          0
        );

        setNumberOfResults(totalHits);
      }

      return (
        <VStack
          alignItems="flex-start"
          p="4"
          sx={{
            ul: {
              listStyle: 'none',
              margin: '0',
              padding: '0',
            },
          }}
        >
          {children}
          {!searchResults?.hits?.length && (
            <div>{tt(s => s.searchInputComponent.noResultsLabel)}</div>
          )}
          {searchResults?.hits?.length > 0 && (
            <Link
              className="text-2xl text-muted-foreground font-semibold mt-6 underline"
              link={`/search?query=${searchState.query}`}
              onClick={e => {
                e.preventDefault();
                // track search viewed event
                trackSearchViewedEvent(searchState.query, searchResults.nbHits);
                // follow the link
                if (countryCode === 'us' && searchState.query) {
                  navigate(`/search?query=${searchState.query}`);
                } else {
                  navigate(`/${countryCode}/search?query=${searchState.query}`);
                }
              }}
            >
              {tt(s => s.searchInputComponent.resultsLabel, {
                query: searchState.query,
              })}
            </Link>
          )}
        </VStack>
      );
    }
  );

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={productIndexName}
      onSearchStateChange={newSearchState =>
        setActiveSearchState(newSearchState)
      }
    >
      {apiError ? (
        <OutsideClickHandler
          onOutsideClick={() => {
            setApiError(false);
            setFocused(false);
          }}
        >
          <Text
            textStyle="bodyLarge"
            bg="inherit"
            pos="absolute"
            w="100%"
            color="white"
            p="1"
          >
            {tt(s => s.searchInputComponent.searchUnavailableLabel)}
          </Text>
        </OutsideClickHandler>
      ) : (
        <OutsideClickHandler onOutsideClick={() => setFocused(false)}>
          <ConnectedSearchForm
            id="connected_search_form"
            onSubmit={e => {
              e.preventDefault();
              navigate(`/search?query=${activeSearchState?.query}`, {
                state: {
                  searchResults: searchRes,
                },
              });
              toggleMobileMenu(false);
              setFocused(false);
            }}
            onFocus={() => setFocused(true)}
            searchFocusUpdater={searchFocusUpdater}
            toggleMobileMenu={toggleMobileMenu}
            numberOfResults={numberOfResults}
          />
          {focused && activeSearchState?.query?.length > 0 && (
            <Box
              left={0}
              bg="white"
              overflowY="scroll"
              zIndex="101"
              w={{ base: '100vw', lg: 'auto' }}
              minW={{ base: '300px', lg: '420px' }}
              pos={{ base: 'fixed', lg: 'absolute' }}
              maxH={{ base: '100vh', lg: '80vh' }}
              boxShadow={{ base: 'none', lg: 'md' }}
              top={{ base: '112', lg: '102%' }}
              h={{ base: 'calc(100vh - 112)', lg: 'max' }}
              borderRadius={{ base: 'none', lg: 'lg' }}
              onKeyDown={e => {
                if ([e.code, e.key].includes('Escape')) {
                  setFocused(false);
                }
              }}
            >
              <Flex justify="left" h="max" maxW="600px" m="auto">
                <Box pb={{ base: 60, lg: 32 }}>
                  {searchIndexes?.includes(productIndexName) && (
                    <Index indexName={productIndexName}>
                      <Configure
                        hitsPerPage={hitsPerPage}
                        filters={getAlgoliaProductsFilter()}
                        clickAnalytics
                      />
                      <Results indexName={productIndexName}>
                        <Text textStyle="h3" as="h2">
                          {tt(s => s.searchInputComponent.productsLabel)}
                        </Text>
                        <Hits hitComponent={CustomSearchFormHit} />
                      </Results>
                    </Index>
                  )}
                  {searchIndexes?.includes(blogIndexName) && (
                    <Index indexName={blogIndexName}>
                      <Configure
                        hitsPerPage={hitsPerPage}
                        filters={getAlgoliaBlogsFilter()}
                        clickAnalytics
                      />
                      <Results indexName={blogIndexName}>
                        <Text textStyle="h3" as="h2">
                          {tt(s => s.searchInputComponent.blogsLabel)}
                        </Text>
                        <Hits hitComponent={CustomSearchFormHit} />
                      </Results>
                    </Index>
                  )}
                </Box>
              </Flex>
            </Box>
          )}
        </OutsideClickHandler>
      )}
    </InstantSearch>
  );
}

NavSearch.propTypes = {
  toggleMobileMenu: PropTypes.func,
  searchIndexes: PropTypes.arrayOf(PropTypes.string).isRequired,
  hitsPerPage: PropTypes.number,
  searchFocusUpdater: PropTypes.number,
};

NavSearch.defaultProps = {
  toggleMobileMenu: () => {},
  hitsPerPage: 5,
  searchFocusUpdater: 1,
};

export default NavSearch;
